import {
  AlertCircle,
  Building,
  Check,
  ChevronDown,
  Search,
  X,
} from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDebounce } from "use-debounce"

import { useBuildingsInfiniteScroll } from "@/features/building/api/get-buildings-infinite-scroll"
import { useBuildingsManagedInfiniteScroll } from "@/features/building/api/get-buildings-managed"
import { BuildingItem } from "@/features/building/types/api"
import { useUser } from "@/lib/auth"
import { cn } from "@/utils/classes"
import { isSuperAdmin } from "@/utils/user"

import { Button } from "../ui/button"
import { Input } from "../ui/form"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/overlays"
import { Badge, Spinner } from "../ui/statuses"
import { Alert, AlertDescription, AlertTitle } from "../ui/statuses/alert"
import { ScrollArea, Separator } from "../ui/surfaces"

type CachedSelectedType = {
  id: number
  name: string
  building_company: {
    id: number
    name: string
  }
}

const getStoredBuilding = (): CachedSelectedType | null => {
  const stored = localStorage.getItem("selected_building")
  return stored ? JSON.parse(stored) : null
}

export const BuildingSwitcher = () => {
  const { t } = useTranslation("common", { keyPrefix: "BuildingSwitcher" })
  const { data: user } = useUser()

  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<CachedSelectedType | null>(
    getStoredBuilding()
  )
  const [search, setSearch] = useState("")
  const [debouncedSearch] = useDebounce(search, 1000)

  const isUserSuperAdmin = isSuperAdmin(user ?? null)

  // Use different hooks based on user role
  const buildingsManagedQuery = useBuildingsManagedInfiniteScroll({
    search: debouncedSearch,
    queryConfig: {
      enabled: !isUserSuperAdmin,
    } as any,
  })

  const buildingsQuery = useBuildingsInfiniteScroll({
    search: debouncedSearch,
    queryConfig: {
      enabled: isUserSuperAdmin,
    } as any,
  })

  // Use the appropriate query based on role
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    isUserSuperAdmin ? buildingsQuery : buildingsManagedQuery

  // Handle null items from API response and filter out any null values
  const buildings =
    data?.pages
      .flatMap((page) => page.items || [])
      .filter((building): building is BuildingItem => building !== null) || []

  // Check if user has no buildings assigned (items is null or empty array)
  const hasNoBuildings =
    !isLoading &&
    buildings.length === 0 &&
    (data?.pages.some((page) => page.items === null) ||
      (data?.pages.length > 0 && data.pages[0].meta.total === 0))

  useEffect(() => {
    if (!selected && !hasNoBuildings) setOpen(true)
  }, [selected, hasNoBuildings])

  useEffect(() => {
    if (hasNoBuildings) setOpen(false)
  }, [hasNoBuildings])

  const handleSelect = (building: BuildingItem) => {
    const buildingData = {
      id: building.id,
      name: building.name,
      building_company: {
        id: building.building_company?.id || 0,
        name: building.building_company?.name || t("NoCompanyAssigned"),
      },
    }
    localStorage.setItem("selected_building", JSON.stringify(buildingData))
    window.dispatchEvent(new Event("selected-building-changed"))
    setSelected(buildingData)
    setOpen(false)
    setSearch("")
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    setSelected(null)
    localStorage.removeItem("selected_building")
    window.dispatchEvent(new Event("selected-building-changed"))
  }

  const selectedBuilding = selected

  const hasStored = !selectedBuilding

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (
      scrollTop + clientHeight >= scrollHeight - 20 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage()
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full text-left justify-start rounded-lg p-2.5 cursor-pointer group truncate lg:max-w-sm"
        >
          <div className="flex items-center justify-between gap-3 w-full truncate">
            <div className="flex items-center gap-2 flex-1 overflow-hidden">
              <Building className="size-[22px] flex-none shrink-0" />
              {selectedBuilding ? (
                <div className="flex flex-col gap-0 flex-1 overflow-hidden">
                  <span className="font-semibold text-xs truncate">
                    {selectedBuilding.name || "-"}
                  </span>
                  <span className="text-[10px] text-foreground/80 truncate w-28 md:w-full">
                    {selectedBuilding.building_company?.name ||
                      "NoCompanyAssigned"}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground flex-1 truncate">
                  {t("SelectBuilding")}
                </span>
              )}
            </div>
            <ChevronDown className="size-5 flex-none text-primary group-data-[state=open]:rotate-180 transition-transform" />
          </div>
        </Button>
      </DialogTrigger>

      <DialogContent
        className="max-w-md p-0 gap-0"
        closeButtonClassName={cn(hasStored && !hasNoBuildings && "hidden")}
        onInteractOutside={(e) => {
          if (!hasNoBuildings) e.preventDefault()
        }}
      >
        <DialogHeader className="p-0 space-y-0 items-start">
          <DialogTitle className="py-4 px-6">{t("Title")}</DialogTitle>
          <Separator />
          <DialogDescription className="hidden" />
        </DialogHeader>
        <div className="flex flex-col gap-4 p-6">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 -translate-y-1/2 text-foreground size-5" />
            <Input
              placeholder={t("SearchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-3.5"
            />
          </div>

          <Separator />

          {selectedBuilding && (
            <div className="bg-muted rounded-lg py-2 px-3.5 flex items-center justify-between">
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-center gap-2">
                  <Building className="size-[22px] flex-none" />
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm line-clamp-1">
                        {selectedBuilding?.name ? selectedBuilding.name : "-"}
                      </span>
                      <Badge variant="success_light">Active</Badge>
                    </div>
                    <span className="text-xs text-foreground/80 line-clamp-1">
                      {selectedBuilding?.building_company
                        ? selectedBuilding.building_company.name
                        : t("NoCompanyAssigned")}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClear}
                  className="!p-0 h-auto justify-end"
                >
                  <X className="text-muted-foreground size-6" />
                </Button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center h-[300px] items-center">
              <Spinner className="size-6 animate-spin" />
            </div>
          ) : hasNoBuildings ? (
            <div className="max-h-[300px] flex justify-center">
              <Alert variant="warning" className="max-w-md">
                <AlertCircle className="size-5" />
                <AlertTitle>{t("NoBuildingAssignedTitle")}</AlertTitle>
                <AlertDescription className="mt-2">
                  {t("NoBuildingAssignedDescription")}
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <ScrollArea
              className="h-[300px] rounded-lg"
              onScroll={handleScroll}
            >
              <div className="bg-muted p-2 flex flex-col gap-2">
                {buildings && buildings.length > 0 ? (
                  buildings.map((building) => (
                    <Button
                      key={building.id}
                      onClick={() => handleSelect(building)}
                      type="button"
                      variant="outline"
                      className={cn(
                        "w-full text-left justify-start hover:bg-foreground/5 h-auto rounded-lg py-3 px-4 cursor-pointer transition-all disabled:opacity-100",
                        selected?.id === building.id &&
                          "text-inherit border border-primary hover:bg-primary/10"
                      )}
                      disabled={selected?.id === building.id}
                    >
                      <div className="flex items-center justify-between gap-3 w-full">
                        <div className="flex items-center gap-2">
                          <Building className="size-[22px] flex-none" />
                          <div className="flex flex-col gap-0.5">
                            <span className="font-semibold text-sm line-clamp-1">
                              {building.name || "-"}
                            </span>
                            <span className="text-xs text-foreground/80 line-clamp-1">
                              {building.building_company?.name ||
                                t("NoCompanyAssigned")}
                            </span>
                          </div>
                        </div>
                        {selected?.id === building.id && (
                          <Check className="text-primary size-5" />
                        )}
                      </div>
                    </Button>
                  ))
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-sm text-center text-muted-foreground py-4">
                    {t("NoBuildingsFound")}
                  </div>
                )}
                {isFetchingNextPage && (
                  <div className="flex justify-center py-2">
                    <Spinner className="size-6 animate-spin" />
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
